import React, { useCallback } from 'react';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import { Equation, SectionUnitsEnum } from '@/__generated__/graphql';
import { keyFromTitle } from '../../../utils';

const FIELD_UNIT_OPTIONS = [
  { label: 'USD', value: SectionUnitsEnum.Usd },
  { label: 'GBP', value: SectionUnitsEnum.Gbp },
  { label: 'Hours', value: SectionUnitsEnum.Hours },
  { label: 'Area', value: SectionUnitsEnum.Area },
  { label: '°F', value: SectionUnitsEnum.DegreeFahrenheit },
  { label: '°C', value: SectionUnitsEnum.DegreeCelsius },
  { label: 'mils', value: SectionUnitsEnum.Mils },
  { label: 'ppm', value: SectionUnitsEnum.Ppm },
  { label: '%', value: SectionUnitsEnum.Percentage },
  { label: 'N/A', value: SectionUnitsEnum.Na },
];

interface EquationFormProps {
  equation: Equation;
  mode: 'ReadOnly' | 'Edit';
  isNewEquation: boolean;
  onUpdate: (updates: Partial<Equation>) => void;
  fieldUnit: SectionUnitsEnum | null;
  displayInLogAggregation: boolean;
  onFieldUnitChange: (fieldUnit: SectionUnitsEnum | null) => void;
  onDisplayInLogAggregationChange: (value: boolean) => void;
  nameError?: boolean;
  nameHelperText?: string;
}

export const EquationForm = React.memo(
  ({
    equation,
    mode,
    isNewEquation,
    onUpdate,
    fieldUnit,
    displayInLogAggregation,
    onFieldUnitChange,
    onDisplayInLogAggregationChange,
    nameError,
    nameHelperText,
  }: EquationFormProps) => {
    const handleNameChange = useCallback(
      (newName: string) => {
        const updates: Partial<Equation> = { name: newName };

        if (isNewEquation) {
          updates.key = keyFromTitle(newName);
        }

        onUpdate(updates);
      },
      [isNewEquation, onUpdate]
    );

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Equation Name"
              value={equation.name ?? ''}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={mode === 'ReadOnly'}
              error={nameError}
              helperText={nameHelperText}
            />

            <TextField
              fullWidth
              select
              label="Field Unit"
              value={fieldUnit ?? SectionUnitsEnum.Na}
              onChange={(e) => {
                const value = e.target.value as SectionUnitsEnum;
                onFieldUnitChange(value ?? null);
              }}
              disabled={mode === 'ReadOnly'}
            >
              {FIELD_UNIT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Checkbox
                  checked={displayInLogAggregation}
                  disabled={mode === 'ReadOnly'}
                  onChange={(event) => onDisplayInLogAggregationChange(event.target.checked)}
                />
              }
              label="Display in Platform Table"
            />
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Equation Key"
            value={equation.key ?? ''}
            disabled
            helperText={
              isNewEquation
                ? 'Auto-generated from equation name'
                : 'Existing equation key (read-only)'
            }
          />
        </Grid>
      </Grid>
    );
  }
);
